import { useEffect, useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { Network, Send, Loader2, Plus, X, Trash2, CheckCircle2 } from "lucide-react";
import { api } from "@/lib/api";
import { useTelecomAccess } from "@/hooks/useTelecomAccess";
import { SectorNoHabilitadoPanel } from "@/components/SectorNoHabilitadoPanel";
import {
  CapturaDocumentos,
  subirDocumentosCapturados,
  type DocumentoCapturado,
} from "@/components/CapturaDocumentos";
import {
  Alert,
  Button,
  Card,
  DataField,
  FormField,
  Input,
  PageContainer,
  PageHeader,
  Select,
  Textarea,
} from "@/components/ui";

type TipoEntidad = "FILIAL" | "SUBSIDIARIA" | "IMPORTADOR";
type Accion = "ALTA" | "BAJA";
type TipoCambio = "AMPLIACION" | "REDUCCION" | "MIXTO";

interface Pais {
  id: string;
  codigo: string;
  nombre: string;
}

interface FilialDetalle {
  id: string;
  tipoEntidad: TipoEntidad;
  razonSocial: string;
  rfc: string | null;
  paisId: string | null;
  paisNombre: string | null;
  domicilio: string | null;
  relacionConTitular: string | null;
  consec: number;
}

interface CertificadoDetalle {
  id: string;
  numCertificado: string;
  fechaEmision: string;
  titularRazonSocial: string | null;
  productoNombre: string;
  productoModelo: string;
  marca: string;
  filiales: FilialDetalle[];
}

/** Entidad en el editor (DEBE DECIR). `uiKey` es estable durante la edición. */
interface Entidad {
  uiKey: string;
  tipoEntidad: TipoEntidad;
  razonSocial: string;
  rfc: string;
  paisId: string;
  domicilio: string;
  relacionConTitular: string;
}

interface Movimiento {
  tipoEntidad: TipoEntidad;
  razonSocial: string;
  rfc: string | null;
  paisId: string | null;
  domicilio: string | null;
  relacionConTitular: string | null;
  accion: Accion;
}

const CONTEXTO_TIPO = "SOLICITUD_TELECOM";
const TAG_DOCS = "TELECOM_AMP_FILIALES";

const TIPOS: { codigo: TipoEntidad; label: string }[] = [
  { codigo: "FILIAL", label: "Filial" },
  { codigo: "SUBSIDIARIA", label: "Subsidiaria" },
  { codigo: "IMPORTADOR", label: "Importador" },
];
const tipoLabel = (t: TipoEntidad) => TIPOS.find((x) => x.codigo === t)?.label ?? t;

/** Clave de comparación (espejo del backend): tipo + RFC (o razón social si no hay RFC). */
function normalizar(s: string): string {
  return s.trim().replace(/\s+/g, " ").toUpperCase();
}
function claveFilial(tipoEntidad: string, rfc: string | null, razonSocial: string): string {
  const ident = rfc && rfc.trim() ? normalizar(rfc) : normalizar(razonSocial);
  return `${normalizar(tipoEntidad)}::${ident}`;
}

/**
 * Pantalla dedicada de Ampliación / Reducción de Filiales, Subsidiarias y/o
 * Importadores, cuarto y último trámite de la familia "Ampliación / Reducción".
 * Se llega desde la Selección de Certificado con ?cert=<certificadoId>.
 *
 * A diferencia de las otras tres variantes, las filiales se modelan como
 * MANIFESTACIÓN INLINE (datos estructurados, sin Registro Legal independiente).
 * El alcance vigente es la lista AUTORITATIVA del certificado (detalle.filiales,
 * poblada en la emisión). La lista se precarga editable; al enviar se calcula el
 * delta contra el alcance original (agregadas = ALTA, removidas = BAJA) y se
 * deriva el tipo de movimiento. El backend (crearAmpliacionFiliales) recibe el
 * contrato movimientos[] con los datos de cada entidad.
 */
export function AmpliacionFilialesAltaPage() {
  const access = useTelecomAccess();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const certId = params.get("cert") ?? "";

  const [entidades, setEntidades] = useState<Entidad[]>([]);
  const [seeded, setSeeded] = useState(false);
  const [motivo, setMotivo] = useState("");
  const [docs, setDocs] = useState<DocumentoCapturado[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [modalOpen, setModalOpen] = useState(false);

  const [enviando, setEnviando] = useState(false);
  const [solicitudCreada, setSolicitudCreada] = useState<string | null>(null);

  const qCert = useQuery<CertificadoDetalle>({
    queryKey: ["telecom-certificado", certId],
    queryFn: async () =>
      (await api.get<CertificadoDetalle>(`/api/sector/telecom/certificados/${certId}`)).data,
    enabled: access.allowed && !!certId,
  });
  const qPaises = useQuery<Pais[]>({
    queryKey: ["telecom-paises"],
    queryFn: async () => (await api.get<Pais[]>("/api/sector/telecom/paises")).data,
    enabled: access.allowed,
  });
  const paisNombre = (id: string | null) =>
    id ? qPaises.data?.find((p) => p.id === id)?.nombre ?? null : null;

  // Alcance vigente (base contra la que se calcula el delta).
  const base = useMemo<FilialDetalle[]>(() => qCert.data?.filiales ?? [], [qCert.data]);

  // Precargar el editor con el alcance actual una sola vez.
  useEffect(() => {
    if (!seeded && qCert.isSuccess) {
      setEntidades(
        base.map((f) => ({
          uiKey: claveFilial(f.tipoEntidad, f.rfc, f.razonSocial),
          tipoEntidad: f.tipoEntidad,
          razonSocial: f.razonSocial,
          rfc: f.rfc ?? "",
          paisId: f.paisId ?? "",
          domicilio: f.domicilio ?? "",
          relacionConTitular: f.relacionConTitular ?? "",
        })),
      );
      setSeeded(true);
    }
  }, [seeded, qCert.isSuccess, base]);

  // Delta calculado contra el alcance original → movimientos para el backend.
  const movimientos = useMemo<Movimiento[]>(() => {
    const baseKeys = new Set(base.map((f) => claveFilial(f.tipoEntidad, f.rfc, f.razonSocial)));
    const actualKeys = new Set(
      entidades.map((e) => claveFilial(e.tipoEntidad, e.rfc, e.razonSocial)),
    );
    const mv: Movimiento[] = [];
    entidades.forEach((e) => {
      if (!baseKeys.has(claveFilial(e.tipoEntidad, e.rfc, e.razonSocial))) {
        mv.push({
          tipoEntidad: e.tipoEntidad,
          razonSocial: e.razonSocial,
          rfc: e.rfc.trim() || null,
          paisId: e.paisId || null,
          domicilio: e.domicilio.trim() || null,
          relacionConTitular: e.relacionConTitular.trim() || null,
          accion: "ALTA",
        });
      }
    });
    base.forEach((f) => {
      if (!actualKeys.has(claveFilial(f.tipoEntidad, f.rfc, f.razonSocial))) {
        mv.push({
          tipoEntidad: f.tipoEntidad,
          razonSocial: f.razonSocial,
          rfc: f.rfc,
          paisId: f.paisId,
          domicilio: f.domicilio,
          relacionConTitular: f.relacionConTitular,
          accion: "BAJA",
        });
      }
    });
    return mv;
  }, [entidades, base]);

  const hayAlta = movimientos.some((m) => m.accion === "ALTA");
  const hayBaja = movimientos.some((m) => m.accion === "BAJA");
  const tipoMovimiento: TipoCambio | null =
    hayAlta && hayBaja ? "MIXTO" : hayAlta ? "AMPLIACION" : hayBaja ? "REDUCCION" : null;
  const hayCambios = movimientos.length > 0;

  const agregar = (ent: Omit<Entidad, "uiKey">) => {
    const key = claveFilial(ent.tipoEntidad, ent.rfc, ent.razonSocial);
    if (entidades.some((e) => claveFilial(e.tipoEntidad, e.rfc, e.razonSocial) === key)) {
      setError("Esa entidad ya está en la lista.");
      return false;
    }
    setError(null);
    setEntidades((arr) => [...arr, { ...ent, uiKey: key }]);
    return true;
  };
  const quitar = (uiKey: string) => setEntidades((arr) => arr.filter((e) => e.uiKey !== uiKey));

  const enviar = async () => {
    if (!hayCambios) {
      setError("No hay cambios en las entidades respecto al alcance actual del certificado.");
      return;
    }
    setError(null);
    setEnviando(true);
    try {
      let id = solicitudCreada;
      if (!id) {
        id = (
          await api.post<{ id: string }>("/api/sector/telecom/solicitudes/ampliacion-filiales", {
            certificadoBaseId: certId,
            tipoMovimiento,
            movimientos,
            motivo: motivo.trim() || null,
          })
        ).data.id;
        setSolicitudCreada(id);
      }
      const fallidos = docs.length
        ? await subirDocumentosCapturados(docs, setDocs, CONTEXTO_TIPO, id)
        : 0;
      if (fallidos > 0) {
        setError(
          `La solicitud se registró, pero ${fallidos} documento(s) no se pudieron subir. ` +
            `Reintenta los marcados en rojo o continúa y adjúntalos desde el detalle de la solicitud.`,
        );
        setEnviando(false);
        return;
      }
      navigate(`/sector/telecom/solicitudes/${id}/success`);
    } catch (e: any) {
      setError(
        e?.response?.data?.detail ?? e?.response?.data?.message ?? "No se pudo registrar la solicitud.",
      );
      setEnviando(false);
    }
  };

  if (access.loading) {
    return (
      <div className="flex items-center justify-center p-12 text-sm text-fg-subtle">
        <Loader2 className="mr-2 h-4 w-4 animate-spin" /> Validando permisos…
      </div>
    );
  }
  if (!access.allowed) {
    return (
      <SectorNoHabilitadoPanel
        sectorNombre="Telecomunicaciones"
        sectorCodigo="TELECOM"
        titularRazonSocial={access.titularRazonSocial}
        sinContexto={access.sinContexto}
      />
    );
  }
  if (!certId) {
    return (
      <Alert variant="warning">
        Falta el certificado. Vuelve a la{" "}
        <Link to="/telecom/servicios/seleccionar-certificado?s=ampliacion-filiales" className="font-medium underline">
          selección de certificado
        </Link>.
      </Alert>
    );
  }

  const bloqueado = !!solicitudCreada;

  return (
    <PageContainer width="form">
      <PageHeader
        backTo="/telecom/servicios/seleccionar-certificado?s=ampliacion-filiales"
        backLabel="Cambiar certificado"
        icon={<Network className="h-5 w-5" />}
        title="Ampliación / Reducción de Filiales, Subsidiarias y/o Importadores"
        subtitle="Edita las entidades del alcance vigente; el sistema detecta automáticamente las altas y bajas."
      />

      <Alert variant="info">
        Cada entidad se captura como manifestación (tipo, razón social, RFC, país, domicilio y relación con el titular). El resto del alcance (producto, normas, países) se conserva sin cambios.
      </Alert>

      <Card title="Certificado seleccionado">
        {qCert.isLoading ? (
          <div className="text-sm text-fg-subtle">Cargando certificado…</div>
        ) : qCert.error || !qCert.data ? (
          <div className="text-sm text-estado-warn">No se pudo cargar el certificado seleccionado.</div>
        ) : (
          <dl className="grid gap-4 sm:grid-cols-2">
            <DataField label="Núm. de Certificado" value={<span className="font-mono">{qCert.data.numCertificado}</span>} />
            <DataField label="Razón Social" value={qCert.data.titularRazonSocial ?? "—"} />
            <DataField label="Producto" value={qCert.data.productoNombre} />
            <DataField label="Modelo principal" value={<span className="font-mono">{qCert.data.productoModelo}</span>} />
          </dl>
        )}
      </Card>

      {/* DICE: entidades actuales (solo lectura) */}
      <Card title="DICE — entidades actuales">
        {qCert.isLoading ? (
          <div className="text-sm text-fg-subtle">Cargando alcance del certificado…</div>
        ) : base.length === 0 ? (
          <p className="text-sm text-fg-subtle">El certificado no tiene filiales, subsidiarias ni importadores registrados en su alcance.</p>
        ) : (
          <EntidadesTabla
            filas={base.map((f) => ({
              tipoEntidad: f.tipoEntidad,
              razonSocial: f.razonSocial,
              rfc: f.rfc,
              paisNombre: f.paisNombre,
            }))}
          />
        )}
      </Card>

      {/* DEBE DECIR: editor de entidades */}
      <Card title="DEBE DECIR — entidades del nuevo alcance">
        {entidades.length === 0 ? (
          <p className="text-sm text-fg-subtle">Sin entidades en el alcance. Agrega filiales, subsidiarias o importadores.</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b text-left text-xs font-semibold uppercase tracking-wide text-fg-subtle">
                  <th className="py-2 pr-3">Tipo</th>
                  <th className="py-2 pr-3">Razón Social</th>
                  <th className="py-2 pr-3">RFC</th>
                  <th className="py-2 pr-3">País</th>
                  {!bloqueado && <th className="py-2" />}
                </tr>
              </thead>
              <tbody>
                {entidades.map((e) => (
                  <tr key={e.uiKey} className="border-b last:border-0">
                    <td className="py-2 pr-3">{tipoLabel(e.tipoEntidad)}</td>
                    <td className="py-2 pr-3">{e.razonSocial}</td>
                    <td className="py-2 pr-3 font-mono">{e.rfc || "—"}</td>
                    <td className="py-2 pr-3">{paisNombre(e.paisId) ?? "—"}</td>
                    {!bloqueado && (
                      <td className="py-2 text-right">
                        <button
                          type="button"
                          onClick={() => quitar(e.uiKey)}
                          className="cursor-pointer rounded p-1 text-estado-err transition hover:bg-estado-err-bg"
                          aria-label={`Quitar ${e.razonSocial}`}
                        >
                          <Trash2 className="h-4 w-4" />
                        </button>
                      </td>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {!bloqueado && (
          <div className="mt-3">
            <Button type="button" variant="secondary" icon={<Plus className="h-4 w-4" />} onClick={() => setModalOpen(true)}>
              Agregar filial / subsidiaria / importador
            </Button>
          </div>
        )}

        {/* Resumen de cambios detectados */}
        <div className="mt-4">
          <div className="text-xs font-semibold uppercase tracking-wide text-fg-subtle">
            Cambios detectados{hayCambios ? ` (${movimientos.length})` : ""}
          </div>
          {!hayCambios ? (
            <p className="mt-1 text-sm text-fg-subtle">
              Aún no hay cambios respecto al alcance actual. Agrega o quita entidades arriba.
            </p>
          ) : (
            <div className="mt-2 flex flex-wrap gap-1.5">
              {movimientos.map((m) => (
                <span
                  key={`${m.accion}-${claveFilial(m.tipoEntidad, m.rfc, m.razonSocial)}`}
                  className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[11px] font-medium ${
                    m.accion === "ALTA" ? "bg-estado-ok-bg text-estado-ok" : "bg-estado-err-bg text-estado-err"
                  }`}
                >
                  {m.accion === "ALTA" ? "+" : "−"} {tipoLabel(m.tipoEntidad)}: {m.razonSocial}
                </span>
              ))}
            </div>
          )}
        </div>

        <FormField label="Justificación / comentarios" className="mt-4">
          <Textarea
            value={motivo}
            onChange={(e) => setMotivo(e.target.value)}
            disabled={bloqueado}
            rows={3}
            placeholder="Explica el motivo del cambio de entidades (relación con el titular, soporte legal, etc.)."
          />
        </FormField>
      </Card>

      {/* Documentos de soporte — parte de la captura */}
      <CapturaDocumentos
        tag={TAG_DOCS}
        titulo="Documentos de soporte"
        ayuda="Adjunta los documentos del trámite (certificado vigente, soporte legal, identificación fiscal). Se subirán junto con la solicitud al enviarla."
        docs={docs}
        onChange={setDocs}
        disabled={enviando}
      />

      {error && <Alert variant="error">{error}</Alert>}

      <div className="flex flex-col-reverse gap-2 sm:flex-row sm:justify-end">
        <Link to="/telecom/servicios/seleccionar-certificado?s=ampliacion-filiales">
          <Button variant="secondary" className="w-full sm:w-auto">Regresar</Button>
        </Link>
        {bloqueado ? (
          <>
            <Button variant="secondary" loading={enviando} onClick={enviar} className="w-full sm:w-auto">
              Reintentar documentos
            </Button>
            <Button
              variant="success"
              icon={<CheckCircle2 className="h-4 w-4" />}
              onClick={() => navigate(`/sector/telecom/solicitudes/${solicitudCreada}/success`)}
              className="w-full sm:w-auto"
            >
              Continuar
            </Button>
          </>
        ) : (
          <Button
            variant="success"
            icon={<Send className="h-4 w-4" />}
            loading={enviando}
            disabled={!hayCambios || qCert.isLoading || !!qCert.error}
            onClick={enviar}
            className="w-full sm:w-auto"
          >
            Enviar solicitud
          </Button>
        )}
      </div>

      {modalOpen && (
        <AgregarEntidadModal
          paises={qPaises.data ?? []}
          onCancel={() => setModalOpen(false)}
          onConfirm={(ent) => {
            if (agregar(ent)) setModalOpen(false);
          }}
        />
      )}
    </PageContainer>
  );
}

/** Tabla simple de entidades (Tipo / Razón Social / RFC / País). */
function EntidadesTabla({
  filas,
}: {
  filas: { tipoEntidad: TipoEntidad; razonSocial: string; rfc: string | null; paisNombre: string | null }[];
}) {
  return (
    <div className="overflow-x-auto">
      <table className="w-full text-sm">
        <thead>
          <tr className="border-b text-left text-xs font-semibold uppercase tracking-wide text-fg-subtle">
            <th className="py-2 pr-3">Tipo</th>
            <th className="py-2 pr-3">Razón Social</th>
            <th className="py-2 pr-3">RFC</th>
            <th className="py-2 pr-3">País</th>
          </tr>
        </thead>
        <tbody>
          {filas.map((f, i) => (
            <tr key={`${f.tipoEntidad}-${f.razonSocial}-${i}`} className="border-b last:border-0">
              <td className="py-2 pr-3">{tipoLabel(f.tipoEntidad)}</td>
              <td className="py-2 pr-3">{f.razonSocial}</td>
              <td className="py-2 pr-3 font-mono">{f.rfc || "—"}</td>
              <td className="py-2 pr-3">{f.paisNombre ?? "—"}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

/** Modal de captura de una entidad (alta). RFC/país/domicilio/relación opcionales. */
function AgregarEntidadModal({
  paises,
  onCancel,
  onConfirm,
}: {
  paises: Pais[];
  onCancel: () => void;
  onConfirm: (ent: Omit<Entidad, "uiKey">) => void;
}) {
  const [tipoEntidad, setTipoEntidad] = useState<TipoEntidad>("FILIAL");
  const [razonSocial, setRazonSocial] = useState("");
  const [rfc, setRfc] = useState("");
  const [paisId, setPaisId] = useState("");
  const [domicilio, setDomicilio] = useState("");
  const [relacionConTitular, setRelacionConTitular] = useState("");
  const [err, setErr] = useState<string | null>(null);

  const confirmar = () => {
    if (!razonSocial.trim()) {
      setErr("La razón social es obligatoria.");
      return;
    }
    onConfirm({
      tipoEntidad,
      razonSocial: razonSocial.trim(),
      rfc: rfc.trim(),
      paisId,
      domicilio: domicilio.trim(),
      relacionConTitular: relacionConTitular.trim(),
    });
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4" role="dialog" aria-modal="true">
      <div className="flex max-h-[90vh] w-full max-w-lg flex-col rounded-lg bg-surface shadow-overlay">
        <div className="flex shrink-0 items-center justify-between border-b px-5 py-3">
          <h3 className="text-base font-semibold text-fg">Agregar entidad</h3>
          <button type="button" onClick={onCancel} className="cursor-pointer rounded p-1 text-fg-faint transition hover:bg-surface-2" aria-label="Cerrar">
            <X className="h-5 w-5" />
          </button>
        </div>

        <div className="min-h-0 flex-1 space-y-4 overflow-y-auto px-5 py-4">
          <FormField label="Tipo de entidad">
            <Select value={tipoEntidad} onChange={(e) => setTipoEntidad(e.target.value as TipoEntidad)}>
              {TIPOS.map((t) => (
                <option key={t.codigo} value={t.codigo}>{t.label}</option>
              ))}
            </Select>
          </FormField>
          <FormField label="Razón social">
            <Input value={razonSocial} onChange={(e) => setRazonSocial(e.target.value)} placeholder="Nombre o razón social de la entidad" />
          </FormField>
          <FormField label="RFC / identificador fiscal (opcional)">
            <Input value={rfc} onChange={(e) => setRfc(e.target.value.toUpperCase())} className="font-mono" placeholder="Puede no aplicar en extranjeros" />
          </FormField>
          <FormField label="País (opcional)">
            <Select value={paisId} onChange={(e) => setPaisId(e.target.value)}>
              <option value="">— Sin especificar —</option>
              {paises.map((p) => (
                <option key={p.id} value={p.id}>{p.nombre}</option>
              ))}
            </Select>
          </FormField>
          <FormField label="Domicilio (opcional)">
            <Input value={domicilio} onChange={(e) => setDomicilio(e.target.value)} placeholder="Domicilio de la entidad" />
          </FormField>
          <FormField label="Relación con el titular (opcional)">
            <Input value={relacionConTitular} onChange={(e) => setRelacionConTitular(e.target.value)} placeholder="Ej. filial 100%, importador autorizado…" />
          </FormField>
          {err && <Alert variant="error">{err}</Alert>}
        </div>

        <div className="flex shrink-0 justify-end gap-2 border-t px-5 py-3">
          <Button type="button" variant="secondary" onClick={onCancel}>Cancelar</Button>
          <Button type="button" variant="primary" icon={<Plus className="h-4 w-4" />} onClick={confirmar}>Agregar</Button>
        </div>
      </div>
    </div>
  );
}
